import { useEffect, useMemo, useState } from 'react';

type DrawerKey = 'why' | 'how' | 'opportunity';
type StepKind =
  | 'welcome'
  | 'source'
  | 'mapping'
  | 'mapping-engine'
  | 'reconcile'
  | 'activate'
  | 'health'
  | 'order-journey'
  | 'evidence-discipline'
  | 'history'
  | 'exception'
  | 'recommendation'
  | 'why-jupiter'
  | 'portfolio'
  | 'next';

interface DemoStep {
  eyebrow: string;
  title: string;
  body: string;
  kind: StepKind;
  proof: string;
  curated?: boolean;
  phase?: 'proven' | 'roadmap';
  notes: Record<DrawerKey, { title: string; body: string }>;
}

const NOTES = {
  semantic: {
    why: {
      title: 'Why this matters',
      body: 'Most integration projects connect applications to applications. Jupiter creates shared operational meaning so existing systems can remain in place while the business gains a consistent model.',
    },
    how: {
      title: 'How Jupiter did this',
      body: 'Source-specific fields are translated into canonical concepts. The mapping is stored with provenance so future sources can reuse the same operating vocabulary instead of rebuilding point-to-point logic.',
    },
    opportunity: {
      title: 'Product opportunity',
      body: 'Jupiter’s product bet is that companies should not repeatedly rebuild this translation layer themselves. The reusable asset is the accumulated standards, mappings, identity decisions, validation rules, and operating definitions—not a one-off integration project.',
    },
  },
  identity: {
    why: {
      title: 'Why this matters',
      body: 'Matching columns is not the same as proving that two records refer to the same real-world company. Alias and duplicate errors can make cross-system analytics confidently wrong.',
    },
    how: {
      title: 'How Jupiter did this',
      body: 'Jupiter treats entity reconciliation as its own governed step: evidence, confidence, a canonical identity, and an explicit human decision when certainty is not high enough.',
    },
    opportunity: {
      title: 'Product opportunity',
      body: 'Identity decisions become reusable operating knowledge. The more sources and companies Jupiter reconciles, the more valuable that accumulated model can become.',
    },
  },
  truth: {
    why: {
      title: 'Why this matters',
      body: 'Operational software should distinguish evidence from absence. Missing delivery history does not mean a supplier is unreliable; it means reliability has not been measured yet.',
    },
    how: {
      title: 'How Jupiter did this',
      body: 'The current supplier foundation preserves NULL performance values rather than coercing missing evidence to zero. Provenance and tenant ownership are retained alongside the canonical supplier record.',
    },
    opportunity: {
      title: 'Product opportunity',
      body: 'Trustworthy intelligence starts with trustworthy semantics. Making known, inferred, and unknown states visible is foundational for recommendations and later governed automation.',
    },
  },
  intelligence: {
    why: {
      title: 'Why this matters',
      body: 'A useful operating layer does more than display metrics. It connects evidence to explicit business definitions, thresholds, exceptions, and eventually decisions.',
    },
    how: {
      title: 'How Jupiter would do this',
      body: 'This guided scenario previews the next layer: connect PO and receipt history to canonical suppliers, calculate measured performance, evaluate it against policy, and surface an exception with its evidence.',
    },
    opportunity: {
      title: 'Product opportunity',
      body: 'Once heterogeneous operational data shares meaning, the same foundation can support supplier risk, inventory optimization, transportation intelligence, forecasting, and portfolio benchmarking.',
    },
  },
  autonomy: {
    why: {
      title: 'Why this matters',
      body: 'Recommendation and authority are different things. A system can identify a good next action without being allowed to execute it.',
    },
    how: {
      title: 'How Jupiter would do this',
      body: 'The product model separates signal, policy, recommendation, authority, action, and outcome. Automation can therefore advance progressively rather than jumping from dashboard to unsupervised execution.',
    },
    opportunity: {
      title: 'Product opportunity',
      body: 'Governed autonomy is more credible when it grows out of a trusted operating model. The maturity path is Observe → Recommend → Approve/Execute → Bounded Autonomy → Learn.',
    },
  },
};

const STEPS: DemoStep[] = [
  {
    eyebrow: '01 · Meet Acme',
    title: 'Acme already runs an end-to-end supply chain. No one system can see all of it.',
    body: 'Orders begin in ERP/OMS, fulfillment moves through WMS and 3PL operations, shipments live in TMS and carrier data, and supplier context sits in still more sources. Jupiter sits above those systems to establish shared meaning and reconstruct the operating story.',
    kind: 'welcome',
    proof: 'Start with the operation, not an application.',
    notes: NOTES.semantic,
  },
  {
    eyebrow: '02 · Connect a source',
    title: 'Start with one bounded source—and build reusable meaning.',
    body: 'Acme’s supplier file is the first wedge. Its terminology belongs to one source system, so Jupiter profiles and translates it into a canonical model without asking Acme to replace the source.',
    kind: 'source',
    proof: 'Connect one source; establish meaning that can be reused elsewhere.',
    notes: NOTES.semantic,
  },
  {
    eyebrow: '03 · Understand',
    title: 'Jupiter interprets the source before importing it.',
    body: 'Instead of a blind column copy, Jupiter translates source-specific fields into canonical supplier concepts and exposes confidence where interpretation is less certain.',
    kind: 'mapping',
    proof: 'Deterministic mapping proposes high-confidence fields and flags ambiguity for review.',
    notes: NOTES.semantic,
  },
  {
    eyebrow: '04 · Inside the mapping engine',
    title: 'Jupiter does not ask an LLM to “figure it out.” It builds an evidence stack.',
    body: 'Today, standards, schema shape, sample values, prior validated mappings, and source context contribute to the proposal. In a later layer, semantic reasoning will rank unresolved residual candidates. The result is a confidence-backed mapping that a human can inspect before it becomes durable logic.',
    kind: 'mapping-engine',
    proof: 'Deterministic evidence proposes candidate meaning; Jupiter persists only the confirmed rule.',
    notes: {
      why: {
        title: 'Why this matters',
        body: 'The credibility of the product depends on making the mapping explainable. A buyer should be able to see why Jupiter thinks a field means Supplier.name, which evidence was strong, and what still requires confirmation.',
      },
      how: {
        title: 'How the mapping is assembled',
        body: 'Jupiter first looks for authoritative identifiers and known standards. It profiles field type, uniqueness, null rate, values, and relationships. It compares that evidence against prior validated mappings and canonical supply-chain concepts. In a later layer, semantic reasoning will rank unresolved residual candidates. Confidence determines whether the mapping can be proposed automatically or needs human review.',
      },
      opportunity: {
        title: 'Why this compounds',
        body: 'A confirmed mapping is not thrown away after onboarding. It becomes reusable product knowledge. The second similar source should require less human work than the first; that declining implementation effort is a core proof that Jupiter is software rather than consulting.',
      },
    },
  },
  {
    eyebrow: '05 · Reconcile',
    title: 'Meaning is not enough. Identity matters too.',
    body: 'Jupiter finds records that may represent the same real-world supplier and makes the reconciliation decision explicit.',
    kind: 'reconcile',
    proof: 'Aliases become governed identity decisions.',
    notes: NOTES.identity,
  },
  {
    eyebrow: '06 · Activate truth',
    title: 'A trusted Foundation is forming underneath Acme.',
    body: 'Supplier is the first proven source, but the canonical registry now spans Supplier, Order, Fulfillment, and Shipment concepts. The same rules—meaning, provenance, missing evidence, and conflict—carry into the operating story.',
    kind: 'activate',
    proof: 'One Foundation · multiple operational domains · explicit evidence state.',
    notes: NOTES.truth,
  },
  {
    eyebrow: '07 · Use the foundation',
    title: 'Supplier Health is one operational view built on the Foundation.',
    body: 'The supplier master becomes useful immediately, while performance remains explicitly unmeasured until operational history exists. This is one domain view—not the boundary of the product.',
    kind: 'health',
    phase: 'proven',
    proof: 'A domain view can be useful without pretending unknown evidence exists.',
    notes: NOTES.truth,
  },
  {
    eyebrow: '08 · Reconstruct the operation',
    title: 'Now zoom out: no system can tell Acme’s whole order story.',
    body: 'Order Journey v1 takes confirmed Foundation mappings from ERP/OMS, WMS, and TMS fragments and reconstructs one provenance-backed operational story. This screen is a frozen snapshot of the CI-verified synthetic Acme fixture—not a hand-authored outcome.',
    kind: 'order-journey',
    phase: 'proven',
    proof: 'Three systems · one operational story · every claim traces to evidence.',
    notes: {
      why: {
        title: 'Why this matters',
        body: 'The order, fulfillment, and shipment systems each know only part of what happened. Jupiter can reconstruct the sequence without replacing those systems or pretending missing evidence exists.',
      },
      how: {
        title: 'How Jupiter did this',
        body: 'Each source was mapped through the same Foundation registry, the confirmed mapping was applied with field-level provenance, and Order Journey merged the canonical facts before computing temporal observations. Caller-supplied mappings are rejected.',
      },
      opportunity: {
        title: 'Product opportunity',
        body: 'The same Foundation that established supplier meaning now reconstructs an order lifecycle. That is the step from a mapper to an operating graph: reusable meaning unlocks a second operational problem.',
      },
    },
  },
  {
    eyebrow: '09 · Inspect the evidence',
    title: 'Jupiter is explicit about what it knows—and what it cannot claim.',
    body: 'The same CI fixture proves observed facts, derived observations, missing evidence, conflicting evidence, and unassessed causality. These labels mirror the API instead of inventing presentation-only language.',
    kind: 'evidence-discipline',
    phase: 'proven',
    proof: 'Known, derived, missing, conflicting, and not assessed are different states.',
    notes: {
      why: {
        title: 'Why this matters',
        body: 'A trustworthy operating layer cannot collapse uncertainty into a confident answer. The product must make absence, disagreement, and causal uncertainty visible to the operator.',
      },
      how: {
        title: 'How Jupiter enforces it',
        body: 'Canonical facts carry observed / missing_source_evidence / conflict status. Derived metrics return null when required evidence is missing or conflicted. Order Journey v1 exposes no causal relationship field and returns causal_confidence = not_assessed.',
      },
      opportunity: {
        title: 'Product opportunity',
        body: 'This epistemic discipline is reusable across every future domain: supplier, order, inventory, transportation, financial truth, and eventually governed recommendations.',
      },
    },
  },
  {
    eyebrow: '10 · Roadmap · Extend the graph',
    title: 'Keep linking evidence around the same operating story.',
    body: 'The next layer connects inventory position, allocation, replenishment, additional carrier events, and financial records to the same canonical entities instead of creating separate analytical silos.',
    kind: 'history',
    curated: true,
    phase: 'roadmap',
    proof: 'Guided preview: reconstructed journey → richer operating graph.',
    notes: NOTES.intelligence,
  },
  {
    eyebrow: '11 · Roadmap · Intelligence',
    title: 'The operating story becomes an exception queue.',
    body: 'Once the graph is rich enough, Jupiter can evaluate reconstructed orders against Acme’s explicit operating definitions and surface the records that need attention—without claiming a cause the evidence does not prove.',
    kind: 'exception',
    curated: true,
    phase: 'roadmap',
    proof: 'Guided preview: evidence + policy → inspectable exception.',
    notes: NOTES.intelligence,
  },
  {
    eyebrow: '12 · Roadmap · Recommendation',
    title: 'Jupiter can recommend the next-best action—and show its authority boundary.',
    body: 'The recommendation is linked to the reconstructed evidence and Acme policy. Execution remains separate: Jupiter is not authorized to change the order, promise, allocation, or shipment in this scenario.',
    kind: 'recommendation',
    curated: true,
    phase: 'roadmap',
    proof: 'Guided preview: recommendation ≠ autonomous execution.',
    notes: NOTES.autonomy,
  },
  {
    eyebrow: '13 · Why Jupiter',
    title: 'Two problems, one operating layer.',
    body: 'First, Jupiter establishes shared meaning across fragmented systems. Then it uses that trusted meaning to reconstruct end-to-end operations those systems cannot explain alone. The same Foundation that normalizes supplier data becomes the basis for order, inventory, transportation, and financial truth.',
    kind: 'why-jupiter',
    proof: 'Problem 1: fragmented meaning. Problem 2: fragmented execution story.',
    notes: {
      why: {
        title: 'Why not DIY?',
        body: 'A capable company can build canonical schemas, profiling, mapping, identity resolution, provenance, validation, and governance. The cost is repeatedly assembling that machinery before visible operational value appears—and maintaining it every time systems or acquisitions change.',
      },
      how: {
        title: 'How Jupiter is different',
        body: 'Today, Jupiter combines supply-chain standards, deterministic profiling, prior validated mappings, confidence, human confirmation, and reconciliation. A later semantic-reasoning layer can help rank unresolved residual candidates; Jupiter will still turn only confirmed results into durable operating logic.',
      },
      opportunity: {
        title: 'Versus broader autonomy platforms',
        body: 'This is a positioning hypothesis to validate: Jupiter starts with a focused Foundation wedge and aims to establish trustworthy shared meaning quickly, especially for midmarket and multi-company environments. Broader autonomy platforms such as Auger position farther downstream around enterprise-wide planning, optimization, agents, and execution.',
      },
    },
  },
  {
    eyebrow: '14 · Portfolio thesis',
    title: 'Now repeat the playbook across companies.',
    body: 'The acquisition thesis is not “buy companies and force them onto one ERP.” It is: acquire operationally solid supply-chain businesses, reconstruct comparable order, inventory, supplier, transportation, and cost truth quickly, then improve each company using a shared operating layer.',
    kind: 'portfolio',
    curated: true,
    proof: 'Guided thesis: company autonomy underneath, comparable operating truth above.',
    notes: {
      why: {
        title: 'Why this matters',
        body: 'Acquisition portfolios often inherit different ERP, WMS, TMS, 3PL, and spreadsheet environments. Full system consolidation can consume time and capital before operational improvement begins. Jupiter’s thesis is to create comparability before replacement.',
      },
      how: {
        title: 'How the portfolio model works',
        body: 'Each company keeps its source systems. Jupiter maps them into the same canonical concepts, retains tenant-level provenance, and then exposes comparable suppliers, inventory, logistics, and KPIs at the portfolio layer. System consolidation remains optional and can be justified by economics rather than ideology.',
      },
      opportunity: {
        title: 'The operating-company thesis',
        body: 'If Jupiter can reduce integration effort and surface repeatable procurement, working-capital, network, and automation opportunities across acquisitions, the software can become proprietary operating infrastructure for a supply-chain-focused holding company—not just a SaaS product.',
      },
    },
  },
  {
    eyebrow: '15 · What we prove next',
    title: 'Next proof: real customer data and commercial pull.',
    body: 'The engineering proof now exists on synthetic data. The next existential question is commercial: can Jupiter establish the same trustworthy cross-system operating story on unfamiliar customer data quickly enough that an operator will fund the pilot and keep paying for the platform?',
    kind: 'next',
    proof: 'Real data · real buyer · bounded pilot · willingness to pay.',
    notes: NOTES.autonomy,
  },
];

const ORDER_JOURNEY_PROOF = {
  orderId: '847291',
  scenario: 'Synthetic Acme demonstration fixture · CI-verified against Order Journey v1',
  metrics: {
    lateByHours: 48,
    cutoffMissHours: 11,
    expediteCost: 184,
  },
  facts: {
    promisedAt: {
      id: 'fact.promised_at',
      value: '2026-10-07T01:00:00.000Z',
      source: 'acme-erp-orders',
      field: 'Promise Date',
      sourceValue: '2026-10-06T20:00:00-05:00',
      status: 'observed',
    },
    pickCompletedAt: {
      id: 'fact.pick_completed_at',
      value: '2026-10-07T00:00:00.000Z',
      source: 'acme-wms-fulfillment',
      field: 'Pick Complete',
      sourceValue: '2026-10-06T19:00:00-05:00',
      status: 'observed',
    },
    carrierCutoffAt: {
      id: 'fact.carrier_cutoff_at',
      value: '2026-10-06T13:00:00.000Z',
      source: 'acme-wms-fulfillment',
      field: 'Carrier Cutoff',
      sourceValue: '2026-10-06T08:00:00-05:00',
      status: 'observed',
    },
    customerEtaAt: {
      id: 'fact.customer_eta_at',
      value: '2026-10-07T01:00:00.000Z',
      source: 'acme-erp-orders',
      field: 'Customer ETA',
      sourceValue: '2026-10-06T20:00:00-05:00',
      status: 'observed',
    },
    deliveredAt: {
      id: 'fact.delivered_at',
      value: '2026-10-09T01:00:00.000Z',
      source: 'acme-tms-shipments',
      field: 'Delivered',
      sourceValue: '2026-10-08T20:00:00-05:00',
      status: 'observed',
    },
    expediteCost: {
      id: 'fact.expedite_cost',
      value: '184',
      source: 'acme-tms-shipments',
      field: 'Expedite',
      sourceValue: '184.00',
      status: 'observed',
    },
  },
  observations: [
    {
      id: 'obs.delivery_after_promise',
      text: 'Delivery occurred 48 hours after the recorded customer promise.',
      evidence: ['fact.promised_at', 'fact.delivered_at'],
    },
    {
      id: 'obs.pick_after_cutoff',
      text: 'Pick completed 11 hours after the recorded carrier cutoff.',
      evidence: ['fact.pick_completed_at', 'fact.carrier_cutoff_at'],
    },
    {
      id: 'obs.delivery_after_customer_eta',
      text: 'Customer ETA precedes actual delivery by 48 hours.',
      evidence: ['fact.customer_eta_at', 'fact.delivered_at'],
    },
    {
      id: 'obs.expedite_charge_recorded',
      text: 'A recorded expedite charge of $184.00 is present on the shipment.',
      evidence: ['fact.expedite_cost'],
    },
  ],
  discipline: {
    observed: {
      label: 'observed',
      text: 'Delivered at 2026-10-09T01:00:00.000Z',
      detail: 'acme-tms-shipments · Delivered · raw 2026-10-08T20:00:00-05:00',
    },
    computed: {
      label: 'derived observation',
      text: 'Delivery occurred 48 hours after the customer promise.',
      detail: 'Evidence: fact.promised_at + fact.delivered_at',
    },
    missing: {
      label: 'missing_source_evidence',
      text: 'Delivery evidence unavailable when shipment fragment is absent.',
      detail: 'late_by_hours = null · no late claim emitted',
    },
    conflict: {
      label: 'conflict',
      text: 'Two delivery cells resolve to different normalized instants.',
      detail: 'TMS 20:00 CT vs carrier feed 18:47 CT · late_by_hours = null',
    },
    causality: {
      label: 'not_assessed',
      text: 'What caused the late delivery?',
      detail: 'causal_findings = [] · causal_confidence = not_assessed',
    },
  },
} as const;

const mappings = [
  ['Vendor Name', 'Supplier.name', '94%'],
  ['Active?', 'Supplier.status', '70% · review'],
  ['Vendor Type', 'Supplier.category', '72% · review'],
];

const DEMO_PROGRESS_KEY = 'jupiter.demo.progress';

interface DemoProgress {
  stepIndex: number;
  drawer: DrawerKey | null;
  marketCaseOpen: boolean;
}

function loadDemoProgress(): DemoProgress {
  try {
    const raw = localStorage.getItem(DEMO_PROGRESS_KEY);
    if (!raw) return { stepIndex: 0, drawer: null, marketCaseOpen: false };
    const parsed = JSON.parse(raw) as Partial<DemoProgress>;
    return {
      stepIndex: Math.max(0, Math.min(STEPS.length - 1, Number(parsed.stepIndex) || 0)),
      drawer: parsed.drawer === 'why' || parsed.drawer === 'how' || parsed.drawer === 'opportunity' ? parsed.drawer : null,
      marketCaseOpen: Boolean(parsed.marketCaseOpen),
    };
  } catch {
    return { stepIndex: 0, drawer: null, marketCaseOpen: false };
  }
}

function DemoPill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{children}</span>;
}

function DemoCanvas({ kind }: { kind: StepKind }) {
  if (kind === 'welcome') {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Acme Furniture · operating landscape</div>
              <div className="mt-2 text-xl font-semibold text-slate-900">One supply chain. Several systems. No single operating story.</div>
            </div>
            <DemoPill>Jupiter sits above the stack</DemoPill>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            ['ERP / OMS', 'Order 847291', 'Promise · customer ETA · allocation'],
            ['WMS / 3PL', 'Fulfillment', 'Pick release · pick complete · carrier cutoff'],
            ['TMS / carrier', 'Shipment', 'Ship · deliver · freight · expedite'],
            ['Supplier sources', 'Trading partners', 'Identity · status · category · country'],
          ].map(([system, domain, detail]) => (
            <div key={system} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{system}</div>
              <div className="mt-3 font-semibold text-slate-900">{domain}</div>
              <div className="mt-2 text-sm leading-5 text-slate-600">{detail}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Foundation</div>
            <div className="mt-2 text-lg font-semibold text-slate-900">Translate → normalize → reconcile → preserve evidence</div>
            <div className="mt-2 text-sm text-slate-600">Establish shared canonical meaning without replacing source systems.</div>
          </div>
          <div className="text-center text-xl text-slate-300">→</div>
          <div className="rounded-2xl border border-slate-300 bg-slate-900 p-5 text-white">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Operating graph</div>
            <div className="mt-2 text-lg font-semibold">Reconstruct what actually happened</div>
            <div className="mt-2 text-sm text-slate-300">Link order, fulfillment, shipment, supplier, inventory, and cost evidence over time.</div>
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'source') {
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">acme_suppliers.csv</div>
        <div className="overflow-x-auto p-5">
          <table className="w-full min-w-[600px] text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="pb-3">Vendor Name</th><th className="pb-3">Supplier #</th><th className="pb-3">Active?</th><th className="pb-3">Vendor Type</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              <tr><td className="py-3 font-medium">Northstar Textiles</td><td>V-1042</td><td>Y</td><td>Textile</td></tr>
              <tr><td className="py-3 font-medium">UPS FREIGHT INC</td><td>V-2098</td><td>Y</td><td>Logistics</td></tr>
              <tr><td className="py-3 font-medium">Lone Pine Hardware</td><td>V-3114</td><td>Y</td><td>Hardware</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (kind === 'mapping') {
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <span className="font-semibold text-slate-800">Semantic interpretation</span>
          <DemoPill>Foundation deterministic output</DemoPill>
        </div>
        <div className="divide-y divide-slate-100">
          {mappings.map(([source, concept, confidence]) => (
            <div key={source} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-3 px-5 py-4 text-sm">
              <span className="font-medium text-slate-800">{source}</span>
              <span className="text-slate-300">→</span>
              <span className="font-mono text-xs text-brand-700">{concept}</span>
              <span className={confidence.includes('review') ? 'text-amber-600' : 'text-emerald-600'}>{confidence}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'mapping-engine') {
    const evidence = [
      ['Known standard / identifier', 'No authoritative identifier found', 'neutral'],
      ['Field name', '“Vendor Name” strongly suggests supplier identity', 'strong'],
      ['Sample values', 'Northstar Textiles · UPS FREIGHT INC · Lone Pine Hardware', 'strong'],
      ['Neighboring fields', 'Vendor_ID · Active Flag · Vendor Type', 'strong'],
      ['Prior mappings', 'Reusable when a tenant has confirmed mappings for the same entity', 'neutral'],
      ['Semantic reasoning', 'Deferred for unresolved / ambiguous residue', 'neutral'],
    ];
    return (
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Source field</div>
            <div className="mt-3 text-2xl font-semibold text-slate-900">Vendor Name</div>
            <div className="mt-2 font-mono text-xs text-slate-500">string · 100% populated · 98% unique</div>
          </div>
          <div className="hidden items-center text-2xl text-slate-300 lg:flex">→</div>
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Proposed meaning</div>
            <div className="mt-3 font-mono text-xl font-semibold text-brand-900">Supplier.name</div>
            <div className="mt-2 text-sm font-semibold text-emerald-600">94% confidence</div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="font-semibold text-slate-900">Evidence Jupiter considered</div>
            <div className="mt-1 text-xs text-slate-500">The model is a proposal engine inside a bounded evidence process—not the source of truth.</div>
          </div>
          <div className="divide-y divide-slate-100">
            {evidence.map(([signal, detail, strength]) => (
              <div key={signal} className="grid gap-2 px-5 py-4 text-sm md:grid-cols-[190px_1fr_auto] md:items-center">
                <span className="font-medium text-slate-700">{signal}</span>
                <span className="text-slate-600">{detail}</span>
                <span className={strength === 'strong' ? 'text-emerald-600' : 'text-slate-400'}>{strength === 'strong' ? '● strong' : '○ none'}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-semibold">Review field: Vendor Type</div>
            <div className="mt-1">Supplier.category 72% · deterministic review threshold</div>
          </div>
          <div className="text-center text-slate-300">human confirms →</div>
          <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900">
            <div className="font-semibold">Durable rule stored</div>
            <div className="mt-1 font-mono text-xs">Acme ERP / Vendor Type → Supplier.category</div>
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'reconcile') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Potential identity match</div>
        <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-xs text-slate-400">Source record</div>
            <div className="mt-1 font-semibold text-slate-900">UPS FREIGHT INC</div>
          </div>
          <div className="text-center text-xl text-slate-300">≈</div>
          <div className="rounded-xl bg-brand-50 p-4">
            <div className="text-xs text-brand-600">Possible canonical match</div>
            <div className="mt-1 font-semibold text-slate-900">United Parcel Service Freight</div>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="font-semibold text-emerald-600">94% confidence</span>
          <button className="btn-primary">Same supplier</button>
          <button className="btn-ghost">Keep separate</button>
          <button className="btn-ghost">Review later</button>
        </div>
      </div>
    );
  }

  if (kind === 'activate') {
    return (
      <div className="space-y-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Supplier', '20 identities', 'Proven source + governed identity'],
            ['Order', 'Canonical registry', 'Order ID · promise · ETA · allocation'],
            ['Fulfillment', 'Canonical registry', 'Pick · cutoff · facility'],
            ['Shipment', 'Canonical registry', 'Carrier · delivery · freight · expedite'],
          ].map(([entity, status, detail]) => (
            <div key={entity} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{entity}</div>
              <div className="mt-2 text-lg font-semibold text-slate-900">{status}</div>
              <div className="mt-2 text-xs leading-5 text-slate-500">{detail}</div>
            </div>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <TruthCard title="Foundation establishes" items={['Canonical meaning', 'Confirmed mappings', 'Entity-scoped concepts', 'Source provenance']} symbol="✓" />
          <TruthCard title="Foundation preserves" items={['Raw source value', 'Missing evidence', 'Conflicting evidence', 'Human decisions']} symbol="△" />
          <TruthCard title="Foundation does not invent" items={['Absent transactions', 'Resolved conflicts', 'Causal explanations', 'Authority to act']} symbol="○" />
        </div>
      </div>
    );
  }

  if (kind === 'health') {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[['20','Total Suppliers'],['20','Active'],['—','Avg Reliability'],['0.0%','Performance Coverage']].map(([v,l]) => (
            <div key={l} className="rounded-2xl border border-slate-200 bg-white p-5"><div className="text-2xl font-semibold text-slate-900">{v}</div><div className="mt-1 text-sm text-slate-500">{l}</div></div>
          ))}
        </div>
        <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5">
          <div className="font-semibold text-sky-900">Supplier master data is live. Performance is not measured yet.</div>
          <div className="mt-1 text-sm text-sky-700">Connect PO receipts, ASN, or delivery history next. Jupiter leaves missing performance unknown rather than treating it as zero.</div>
        </div>
      </div>
    );
  }

  if (kind === 'order-journey') {
    const factRows = [
      ['Promise', ORDER_JOURNEY_PROOF.facts.promisedAt.value, ORDER_JOURNEY_PROOF.facts.promisedAt.source, ORDER_JOURNEY_PROOF.facts.promisedAt.field],
      ['Pick complete', ORDER_JOURNEY_PROOF.facts.pickCompletedAt.value, ORDER_JOURNEY_PROOF.facts.pickCompletedAt.source, ORDER_JOURNEY_PROOF.facts.pickCompletedAt.field],
      ['Delivered', ORDER_JOURNEY_PROOF.facts.deliveredAt.value, ORDER_JOURNEY_PROOF.facts.deliveredAt.source, ORDER_JOURNEY_PROOF.facts.deliveredAt.field],
      ['Expedite', '$' + ORDER_JOURNEY_PROOF.facts.expediteCost.value, ORDER_JOURNEY_PROOF.facts.expediteCost.source, ORDER_JOURNEY_PROOF.facts.expediteCost.field],
    ];
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-brand-200 bg-brand-50 p-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Order #{ORDER_JOURNEY_PROOF.orderId} · reconstructed by Jupiter</div>
            <div className="mt-2 text-xl font-semibold text-slate-900">48 hours after promise · 11 hours after cutoff · $184 recorded expedite charge</div>
          </div>
          <span className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600">Synthetic demo data · CI verified</span>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="grid grid-cols-[140px_1fr_170px_120px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <span>Fact</span><span>Canonical value</span><span>Source</span><span>Field</span>
          </div>
          {factRows.map(([label,value,source,field]) => (
            <div key={label} className="grid grid-cols-[140px_1fr_170px_120px] gap-3 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
              <span className="font-medium text-slate-800">{label}</span>
              <span className="font-mono text-xs text-slate-700">{value}</span>
              <span className="text-slate-600">{source}</span>
              <span className="text-slate-600">{field}</span>
            </div>
          ))}
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          {ORDER_JOURNEY_PROOF.observations.map((observation) => (
            <div key={observation.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="font-mono text-[11px] text-brand-700">{observation.id}</div>
              <div className="mt-2 text-sm font-medium text-slate-900">{observation.text}</div>
              <div className="mt-2 text-xs text-slate-500">Evidence: {observation.evidence.join(' + ')}</div>
            </div>
          ))}
        </div>

        <div className="rounded-xl bg-slate-900 p-4 text-sm text-slate-200">
          <span className="font-semibold text-white">What this is:</span> a frozen presentation of values asserted in the Order Journey v1 CI fixture. The public demo is not making a live branch-only API call.
        </div>
      </div>
    );
  }

  if (kind === 'evidence-discipline') {
    const cards = [
      ['OBSERVED', ORDER_JOURNEY_PROOF.discipline.observed.label, ORDER_JOURNEY_PROOF.discipline.observed.text, ORDER_JOURNEY_PROOF.discipline.observed.detail],
      ['COMPUTED', ORDER_JOURNEY_PROOF.discipline.computed.label, ORDER_JOURNEY_PROOF.discipline.computed.text, ORDER_JOURNEY_PROOF.discipline.computed.detail],
      ['UNKNOWN', ORDER_JOURNEY_PROOF.discipline.missing.label, ORDER_JOURNEY_PROOF.discipline.missing.text, ORDER_JOURNEY_PROOF.discipline.missing.detail],
      ['CONFLICT', ORDER_JOURNEY_PROOF.discipline.conflict.label, ORDER_JOURNEY_PROOF.discipline.conflict.text, ORDER_JOURNEY_PROOF.discipline.conflict.detail],
      ['CAUSALITY', ORDER_JOURNEY_PROOF.discipline.causality.label, ORDER_JOURNEY_PROOF.discipline.causality.text, ORDER_JOURNEY_PROOF.discipline.causality.detail],
    ];
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Synthetic Acme fixture · real v1 response states</div>
          <div className="mt-2 text-xl font-semibold text-slate-900">Jupiter knows what it knows, shows where it knows it from, and does not invent the rest.</div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {cards.map(([heading,status,text,detail], index) => (
            <div key={heading} className={`rounded-2xl border bg-white p-5 ${index === 4 ? 'md:col-span-2 border-violet-200' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{heading}</div>
                <code className="rounded bg-slate-100 px-2 py-1 text-[11px] text-slate-700">{status}</code>
              </div>
              <div className="mt-3 font-medium text-slate-900">{text}</div>
              <div className="mt-2 text-xs leading-5 text-slate-500">{detail}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <span className="font-semibold">Rule:</span> derived observations inherit the uncertainty of their inputs. Missing or conflicting evidence produces a null metric—not a chosen answer.
        </div>
      </div>
    );
  }

  if (kind === 'history') {
    return (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Roadmap · same order, more evidence</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">Order #847291 becomes a connected operating graph.</div>
          </div>
          <DemoPill>Guided scenario</DemoPill>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {[
            ['Inventory position', 'Where was stock available when the promise was made?', 'Future evidence'],
            ['Allocation', 'Which facility or node received the demand?', 'Future evidence'],
            ['Carrier events', 'What happened between tender and delivery?', 'Future evidence'],
            ['Financial truth', 'What cost was expected versus recorded?', 'Future evidence'],
          ].map(([title, question, status]) => (
            <div key={title} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{status}</div>
              <div className="mt-2 font-semibold text-slate-900">{title}</div>
              <div className="mt-2 text-sm leading-5 text-slate-600">{question}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900">
          These records would be linked only after their sources are mapped through Foundation. Missing evidence remains missing rather than being backfilled by the demo.
        </div>
      </div>
    );
  }

  if (kind === 'exception') {
    return (
      <div className="grid gap-4 lg:grid-cols-[1.2fr_.8fr]">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700">Roadmap exception · Order #847291</div>
          <div className="mt-2 text-xl font-semibold text-slate-900">This order deserves operator attention.</div>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <Stat label="After promise" value="48h" />
            <Stat label="Pick after cutoff" value="11h" />
            <Stat label="Recorded expedite" value="$184" />
            <Stat label="Causal confidence" value="Not assessed" />
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-sm font-semibold text-slate-800">What Jupiter may say</div>
          <p className="mt-3 text-sm leading-6 text-slate-600">The evidence meets an Acme exception policy: delivery occurred after the recorded promise and an expedite charge is present.</p>
          <div className="mt-5 text-sm font-semibold text-slate-800">What Jupiter may not say</div>
          <p className="mt-2 text-sm leading-6 text-slate-600">It may not claim the pick timing caused the late delivery, or that the expedite charge was avoidable, unless later evidence supports that conclusion.</p>
        </div>
      </div>
    );
  }

  if (kind === 'recommendation') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Roadmap · governed recommendation</div>
        <h3 className="mt-2 text-xl font-semibold text-slate-900">Review the order’s promise, fulfillment path, and shipment options before changing the customer commitment.</h3>
        <div className="mt-5 grid gap-2 text-sm text-slate-600">
          <div>✓ Evidence: delivery recorded 48 hours after promise</div>
          <div>✓ Evidence: pick completed 11 hours after recorded carrier cutoff</div>
          <div>✓ Evidence: $184 expedite charge recorded</div>
          <div>○ Cause of delay: not assessed</div>
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-3">
          <Stat label="Recommendation" value="Review" />
          <Stat label="Human approval" value="Required" />
          <Stat label="Execution authority" value="None" />
        </div>
        <div className="mt-5 rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm font-medium text-violet-900">Jupiter is not authorized to change the order, promise, allocation, or shipment in this scenario.</div>
      </div>
    );
  }

  if (kind === 'why-jupiter') {
    return (
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">The problem</div>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">Humans become the translation layer.</h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">Teams repeatedly reconcile ERP, WMS, TMS, 3PL, EDI, spreadsheets, and acquired-company data before they can answer operational questions—or reconstruct what happened to a single order.</p>
          </div>
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">How Jupiter works</div>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">Interrogate → propose → confirm → operationalize.</h3>
            <p className="mt-3 text-sm leading-6 text-slate-700">Standards and deterministic profiling constrain the problem today. In a later layer, semantic reasoning will help rank unresolved residual meaning. Humans resolve ambiguity. Confirmed mappings and identities become reusable rules.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Why Jupiter</div>
            <h3 className="mt-2 text-lg font-semibold text-slate-900">Buy the repeated work once.</h3>
            <p className="mt-3 text-sm leading-6 text-slate-600">The value compounds twice: first in reusable mappings, identities, and standards; then in the operating graph Jupiter can reconstruct across orders, inventory, shipments, suppliers, and cost.</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-5 py-4">
            <div className="font-semibold text-slate-900">Positioning hypothesis</div>
            <div className="mt-1 text-xs text-slate-500">A claim to validate with customers—not a claim that competitors cannot do these things.</div>
          </div>
          <div className="grid divide-y divide-slate-100 md:grid-cols-[160px_1fr_1fr_1fr] md:divide-x md:divide-y-0">
            <CompareCell label="" />
            <CompareCell label="DIY / services" strong />
            <CompareCell label="Jupiter" strong brand />
            <CompareCell label="Broader autonomy platform" strong />
          </div>
          {[
            ['Starting point', 'Build the data foundation', 'One painful operational wedge', 'Enterprise planning + execution'],
            ['Primary promise', 'Custom integration', 'Trusted shared meaning quickly', 'Decision orchestration / autonomy'],
            ['Human work', 'Discover + build most mappings', 'Resolve uncertainty and exceptions', 'Implementation + operating-model design'],
            ['Reusable asset', 'Internal project artifacts', 'Mappings, identity, standards, policies', 'Ontology, optimization, agents'],
            ['Expansion path', 'Project by project', 'Foundation → Intelligence → governed autonomy', 'Autonomy across planning/execution'],
          ].map((row) => (
            <div key={row[0]} className="grid divide-y divide-slate-100 border-t border-slate-100 md:grid-cols-[160px_1fr_1fr_1fr] md:divide-x md:divide-y-0">
              <CompareCell label={row[0]} heading />
              <CompareCell label={row[1]} />
              <CompareCell label={row[2]} brand />
              <CompareCell label={row[3]} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (kind === 'portfolio') {
    return (
      <div className="space-y-5">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Acme Furniture</div>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              <div>ERP: Vendor_ID · Vendor Name</div>
              <div>WMS: Acme-specific location codes</div>
              <div>TMS: UPS FREIGHT INC</div>
            </div>
          </div>
          <div className="text-center text-xl font-semibold text-brand-600">Jupiter</div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Beacon Home</div>
            <div className="mt-3 space-y-2 text-sm text-slate-600">
              <div>ERP: SupplierNumber · Trading Partner</div>
              <div>3PL: Beacon-specific facility codes</div>
              <div>TMS: United Parcel Service Freight</div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">Portfolio operating model</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Companies normalized" value="2" />
            <Stat label="Shared supplier candidates" value="2" />
            <Stat label="Comparable KPI domains" value="1" />
            <Stat label="ERP replacements required" value="0" />
          </div>
          <div className="mt-5 rounded-xl bg-white/80 p-4 text-sm text-slate-700">
            <span className="font-semibold">Potential shared supplier:</span> Acme “UPS FREIGHT INC” ≈ Beacon “United Parcel Service Freight”
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          {[
            ['Acquire', 'Keep the operating company intact.'],
            ['Normalize', 'Map sources into a comparable Jupiter model.'],
            ['Improve', 'Find procurement, inventory, network, and automation levers.'],
            ['Repeat', 'Reuse the operating model on the next acquisition.'],
          ].map(([title,body]) => (
            <div key={title} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="font-semibold text-slate-900">{title}</div>
              <div className="mt-2 text-sm leading-5 text-slate-600">{body}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        <Stage title="Foundation" items={['Shared meaning', 'Identity', 'Provenance', 'Reusable mappings']} active />
        <Stage title="Operating graph" items={['Order journey', 'Inventory movement', 'Shipment events', 'Cost linkage']} active />
        <Stage title="Intelligence + action" items={['Explain exceptions', 'Recommend action', 'Optimize', 'Governed automation']} />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ideal first design partner</div>
          <h3 className="mt-2 text-lg font-semibold text-slate-900">A company where people are already reconciling supply-chain truth by hand.</h3>
          <div className="mt-4 space-y-2 text-sm text-slate-600">
            <div>• $100M–$1B manufacturer, distributor, retailer, or logistics-heavy operator</div>
            <div>• multiple ERP / WMS / TMS / 3PL / spreadsheet sources</div>
            <div>• a painful supplier, inventory, or logistics visibility problem</div>
            <div>• executive sponsor willing to share a bounded dataset</div>
          </div>
        </div>

        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-600">First paid pilot</div>
          <h3 className="mt-2 text-lg font-semibold text-slate-900">4–6 weeks · one operational domain · 2–3 source systems</h3>
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <div>✓ Profile and interpret unfamiliar sources</div>
            <div>✓ Establish semantic mappings and identity reconciliation</div>
            <div>✓ Expose provenance, uncertainty, and data-quality gaps</div>
            <div>✓ Deliver a trusted canonical operating model</div>
            <div>✓ Measure whether the second source is easier than the first</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
        <span className="font-semibold">The question to take to market:</span> “If Jupiter can connect the systems you already run, reconstruct what actually happened to an order, and show where time or money was lost—without replacing those systems—is that painful enough to fund?”
      </div>
    </div>
  );
}

function CompareCell({ label, strong = false, brand = false, heading = false }: { label: string; strong?: boolean; brand?: boolean; heading?: boolean }) {
  return (
    <div className={`p-4 text-sm ${brand ? 'bg-brand-50 text-brand-900' : 'text-slate-600'} ${strong || heading ? 'font-semibold' : ''}`}>
      {label}
    </div>
  );
}

function TruthCard({ title, items, symbol }: { title: string; items: string[]; symbol: string }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-5"><div className="text-sm font-semibold text-slate-800">{title}</div><div className="mt-3 space-y-2">{items.map((item)=><div key={item} className="flex gap-2 text-sm text-slate-600"><span className="text-brand-600">{symbol}</span><span>{item}</span></div>)}</div></div>;
}

function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-xl bg-white/70 p-3"><div className="text-xs text-slate-500">{label}</div><div className="mt-1 font-semibold text-slate-900">{value}</div></div>;
}

function Stage({ title, items, active = false }: { title: string; items: string[]; active?: boolean }) {
  return <div className={`rounded-2xl border p-5 ${active ? 'border-brand-200 bg-brand-50' : 'border-slate-200 bg-white'}`}><div className="font-semibold text-slate-900">{title}</div><div className="mt-3 space-y-2">{items.map((item)=><div key={item} className="text-sm text-slate-600">{active ? '✓' : '○'} {item}</div>)}</div></div>;
}

function MarketCase({ onClose }: { onClose: () => void }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-400">Market case</div>
          <h2 className="mt-2 text-3xl font-semibold text-white">Is this problem large, painful, and buyable?</h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-400">This is the evidence and commercial hypothesis behind Jupiter. It is deliberately separate from the product walkthrough so the demo can stay experiential while the market case remains inspectable.</p>
        </div>
        <button onClick={onClose} className="rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-300 hover:bg-white/5">Back to demo</button>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <div className="text-3xl font-semibold text-white">92%</div>
          <div className="mt-2 text-sm font-medium text-slate-200">say operations-tech investments have not fully delivered expected results</div>
          <p className="mt-3 text-xs leading-5 text-slate-500">PwC’s 2025 survey of 610 operations and supply-chain leaders cited integration complexity (47%) and data issues (44%) as the two most common reasons.</p>
          <a className="mt-3 inline-block text-xs font-medium text-brand-300 hover:text-brand-200" href="https://www.pwc.com/us/en/services/consulting/supply-chain-operations/digital-supply-chain-survey.html" target="_blank" rel="noreferrer">PwC · 2025 Digital Trends in Operations ↗</a>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <div className="text-3xl font-semibold text-white">62%</div>
          <div className="mt-2 text-sm font-medium text-slate-200">lack a well-defined process for integrating new and existing data sources</div>
          <p className="mt-3 text-xs leading-5 text-slate-500">McKinsey also found 80% of surveyed organizations had divisions operating in data silos and 82% spent at least one day per week resolving master-data-quality issues.</p>
          <a className="mt-3 inline-block text-xs font-medium text-brand-300 hover:text-brand-200" href="https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/master-data-management-the-key-to-getting-more-from-your-data" target="_blank" rel="noreferrer">McKinsey · Master data management ↗</a>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
          <div className="text-3xl font-semibold text-white">200K</div>
          <div className="mt-2 text-sm font-medium text-slate-200">U.S. middle-market businesses</div>
          <p className="mt-3 text-xs leading-5 text-slate-500">The National Center for the Middle Market defines the segment as $10M–$1B revenue and says it accounts for roughly one-third of U.S. private-sector GDP and employment. Jupiter’s initial hypothesis is narrower: operationally complex companies around $100M–$1B.</p>
          <a className="mt-3 inline-block text-xs font-medium text-brand-300 hover:text-brand-200" href="https://www.middlemarketcenter.org/Media/Documents/MiddleMarketIndicators/2025-Q4/InfoSheet/NCMM%20InfoSheet.pdf" target="_blank" rel="noreferrer">National Center for the Middle Market ↗</a>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Commercial hypotheses</div>
          <div className="mt-4 space-y-4 text-sm leading-6 text-slate-300">
            <p><strong className="text-white">Problem:</strong> fragmented operational meaning is expensive enough that companies will fund fixing one high-value domain at a time.</p>
            <p><strong className="text-white">Product:</strong> standards + deterministic profiling + prior validated mappings + human confirmation can remove a large share of manual discovery work; semantic reasoning is reserved for a later unresolved-residue layer.</p>
            <p><strong className="text-white">Speed:</strong> a bounded domain can reach trustworthy operational truth in 4–6 weeks rather than requiring an enterprise-wide transformation.</p>
            <p><strong className="text-white">Compounding:</strong> the second similar source should be materially easier than the first because Jupiter retains validated mappings, identities, and standards.</p>
            <p><strong className="text-white">Commercial:</strong> the Foundation becomes valuable enough to support a paid pilot and recurring platform spend before full autonomy exists.</p>
          </div>
        </div>

        <div className="rounded-2xl border border-rose-400/20 bg-rose-400/[0.05] p-6">
          <div className="text-xs font-semibold uppercase tracking-wider text-rose-300">What would prove us wrong?</div>
          <div className="mt-4 space-y-3 text-sm leading-6 text-slate-300">
            <div>• customers do not experience this as an economically meaningful problem</div>
            <div>• existing tools solve the same bounded problem cheaply and quickly</div>
            <div>• every implementation still requires bespoke semantic engineering</div>
            <div>• implementation effort does not decline on the second and third sources</div>
            <div>• buyers will not fund Foundation without a finished Intelligence product</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-brand-400/20 bg-brand-500/10 p-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-brand-300">Who should we test this with?</div>
        <h3 className="mt-2 text-xl font-semibold text-white">An operator already paying the “human translation layer” tax.</h3>
        <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-300">$100M–$1B manufacturer, distributor, retailer, or logistics-heavy company; multiple ERP/WMS/TMS/3PL/spreadsheet sources; a painful supplier, inventory, or logistics visibility problem; and an executive sponsor willing to share a bounded dataset.</p>
        <div className="mt-4 text-sm font-medium text-brand-200">The ask: “If Jupiter can establish a trustworthy cross-system operating model in 4–6 weeks without replacing your systems, is that painful enough to fund?”</div>
      </div>
    </div>
  );
}

interface DemoWalkthroughProps {
  onExploreAcmeDashboard: () => void;
  onExit: () => void;
}

export function DemoWalkthrough({ onExploreAcmeDashboard, onExit }: DemoWalkthroughProps) {
  const initialProgress = useMemo(() => loadDemoProgress(), []);
  const [stepIndex, setStepIndex] = useState(initialProgress.stepIndex);
  const [drawer, setDrawer] = useState<DrawerKey | null>(initialProgress.drawer);
  const [marketCaseOpen, setMarketCaseOpen] = useState(initialProgress.marketCaseOpen);
  const step = STEPS[stepIndex];

  useEffect(() => {
    localStorage.setItem(DEMO_PROGRESS_KEY, JSON.stringify({ stepIndex, drawer, marketCaseOpen }));
  }, [stepIndex, drawer, marketCaseOpen]);
  const progress = useMemo(() => ((stepIndex + 1) / STEPS.length) * 100, [stepIndex]);

  function move(next: number) {
    setDrawer(null);
    setMarketCaseOpen(false);
    setStepIndex(Math.max(0, Math.min(STEPS.length - 1, next)));
  }

  function restart() {
    localStorage.removeItem(DEMO_PROGRESS_KEY);
    setStepIndex(0);
    setDrawer(null);
    setMarketCaseOpen(false);
  }

  return (
    <div className="min-h-full bg-slate-950 text-slate-100">
      <header className="border-b border-white/10 bg-slate-950/95 px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3"><div className="text-2xl">⛓️</div><div><div className="font-semibold">Jupiter</div><div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Supply Chain OS · Acme operating walkthrough</div></div></div>
          <div className="flex items-center gap-2">
            <button onClick={restart} className="rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white">Restart demo</button>
            <button onClick={onExit} className="rounded-lg border border-white/10 px-3 py-2 text-sm text-slate-300 hover:bg-white/5">Exit</button>
          </div>
        </div>
      </header>

      <div className="h-1 bg-slate-900"><div className="h-full bg-brand-500 transition-all" style={{ width: `${progress}%` }} /></div>

      <main className={`mx-auto max-w-7xl px-6 py-10 ${marketCaseOpen ? '' : 'grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px]'}`}>
        {marketCaseOpen ? (
          <MarketCase onClose={() => setMarketCaseOpen(false)} />
        ) : (
        <>
        <section>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-400">{step.eyebrow}</div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <h1 className="max-w-4xl text-3xl font-semibold tracking-tight text-white md:text-4xl">{step.title}</h1>
            {step.phase === 'proven' && <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-xs font-medium text-emerald-300">Proven v1</span>}
            {step.phase === 'roadmap' && <span className="rounded-full border border-violet-400/30 bg-violet-400/10 px-2.5 py-1 text-xs font-medium text-violet-300">Roadmap preview</span>}
            {step.curated && step.phase !== 'roadmap' && <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-xs font-medium text-amber-300">Guided scenario</span>}
          </div>
          <p className="mt-4 max-w-3xl text-base leading-7 text-slate-400">{step.body}</p>

          <div className="mt-8 rounded-3xl bg-slate-100 p-5 text-slate-900 shadow-2xl shadow-black/20 md:p-8">
            <DemoCanvas kind={step.kind} />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="text-sm text-slate-500"><span className="font-medium text-slate-300">What this proves:</span> {step.proof}</div>
            <div className="flex gap-2">
              <button disabled={stepIndex === 0} onClick={() => move(stepIndex - 1)} className="rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-slate-300 disabled:opacity-30">Back</button>
              {stepIndex < STEPS.length - 1 ? (
                <button onClick={() => move(stepIndex + 1)} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">Next</button>
              ) : (
                <button onClick={onExploreAcmeDashboard} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">Explore Acme dashboard</button>
              )}
            </div>
          </div>
        </section>

        <aside className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Go deeper</div>
            <div className="mt-3 space-y-2">
              {([
                ['why', 'Why this matters'],
                ['how', 'How Jupiter did this'],
                ['opportunity', 'Product opportunity'],
              ] as Array<[DrawerKey,string]>).map(([key,label]) => (
                <button key={key} onClick={() => setDrawer(drawer === key ? null : key)} className={`w-full rounded-xl border px-4 py-3 text-left text-sm font-medium transition ${drawer === key ? 'border-brand-500 bg-brand-500/10 text-brand-300' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:bg-white/[0.06]'}`}>{label}</button>
              ))}
            </div>
          </div>

          {drawer && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <div className="font-semibold text-white">{step.notes[drawer].title}</div>
              <p className="mt-3 text-sm leading-6 text-slate-400">{step.notes[drawer].body}</p>
            </div>
          )}

          <button
            onClick={() => setMarketCaseOpen(true)}
            className="w-full rounded-xl border border-brand-500/30 bg-brand-500/10 px-4 py-3 text-left text-sm font-medium text-brand-200 transition hover:bg-brand-500/15"
          >
            Read the market case →
          </button>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Progress saved</div>
            <p className="mt-2 text-xs leading-5 text-slate-500">This browser remembers your current step and open notes automatically. Return to this URL later to continue where you left off.</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Demo integrity</div>
            <p className="mt-2 text-xs leading-5 text-slate-500">Steps marked “Proven v1” reflect behavior exercised by the current Foundation / Order Journey CI using synthetic Acme data. “Roadmap preview” screens are not built product capability. The public demo uses frozen verified fixtures rather than implying live customer data or live branch-only APIs.</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Capability boundary</div>
            <div className="mt-2 text-xs leading-5 text-slate-500"><span className="text-emerald-300">Proven v1</span> = implemented + CI exercised. <span className="text-violet-300">Roadmap preview</span> = future layer, shown only to explain direction.</div>
          </div>

          <div className="text-xs text-slate-600">Step {stepIndex + 1} of {STEPS.length} · designed for ~7 minutes</div>
        </aside>
        </>
        )}
      </main>
    </div>
  );
}
